
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, Article, articleFromDB } from '../../lib/supabase';

const News: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState<string>('全部');
  const [selectedCountry, setSelectedCountry] = useState<string>('全部');
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  // 从Supabase加载文章
  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('status', 'published')
        .order('date', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        // 转换数据库格式到前端格式
        const convertedArticles = data.map(articleFromDB);
        setArticles(convertedArticles);
      } else {
        setArticles([]);
      }
    } catch (error) {
      console.error('加载文章失败:', error);
      setArticles([]);
    } finally {
      setIsLoading(false);
    }
  };

  // 根据选择的分类筛选文章
  useEffect(() => {
    let filtered = articles;

    // 如果选择了具体的行业分类
    if (selectedIndustry !== '全部') {
      filtered = filtered.filter(
        article => article.columnType === 'industry' && article.category === selectedIndustry
      );
    }

    // 如果选择了具体的国家分类
    if (selectedCountry !== '全部') {
      filtered = filtered.filter(
        article => article.columnType === 'country' && article.category === selectedCountry
      );
    }

    // 如果同时选择了行业和国家，显示两个分类的文章
    if (selectedIndustry !== '全部' && selectedCountry !== '全部') {
      filtered = articles.filter(
        article =>
          (article.columnType === 'industry' && article.category === selectedIndustry) ||
          (article.columnType === 'country' && article.category === selectedCountry)
      );
    }

    // 按日期排序
    filtered = filtered.sort(
      (a: Article, b: Article) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    setFilteredArticles(filtered);
  }, [articles, selectedIndustry, selectedCountry]);

  const handleArticleClick = (articleId: string) => {
    navigate(`/zh/news/detail/${articleId}`);
  };

  const handleIndustryFilter = (industry: string) => {
    setSelectedIndustry(industry);
    if (industry !== '全部') {
      setSelectedCountry('全部');
    }
  };

  const handleCountryFilter = (country: string) => {
    setSelectedCountry(country);
    if (country !== '全部') {
      setSelectedIndustry('全部');
    }
  };

  // 动态获取分类列表
  const industryCategories = useMemo(() => {
    const categories = ['全部'];
    const industryArticles = articles.filter(article => article.columnType === 'industry');
    const uniqueCategories = [...new Set(industryArticles.map(article => article.category))];
    return categories.concat(uniqueCategories.sort());
  }, [articles]);

  const countryCategories = useMemo(() => {
    const categories = ['全部'];
    const countryArticles = articles.filter(article => article.columnType === 'country');
    const uniqueCategories = [...new Set(countryArticles.map(article => article.category))];
    return categories.concat(uniqueCategories.sort());
  }, [articles]);

  // 获取每个分类的文章数量
  const getIndustryCategoryCount = (category: string) => {
    if (category === '全部') {
      return articles.filter(article => article.columnType === 'industry').length;
    }
    return articles.filter(article => article.columnType === 'industry' && article.category === category).length;
  };

  const getCountryCategoryCount = (category: string) => {
    if (category === '全部') {
      return articles.filter(article => article.columnType === 'country').length;
    }
    return articles.filter(article => article.columnType === 'country' && article.category === category).length;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 flex items-center justify-center gap-2 md:h-20 bg-[#071C74] px-4 py-4 font-medium md:flex-none md:justify-start md:p-2 md:px-3">
        <div className="sticky top-0 flex h-7 items-center justify-between md:h-20 w-full max-w-7xl mx-auto">
          <div className="adi-header-left">
            <a href="/">
              <img
                alt="ADI Logo"
                width={125}
                height={56}
                src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/3900b388860a5efa532a02080a385ec7.png"
                className="h-14 w-auto"
              />
            </a>
          </div>

          <div className="hidden md:flex items-center space-x-4 text-white">
            <div className="relative">
              <div className="flex bg-white/10 backdrop-blur-sm rounded-full p-1">
                <button
                  className="px-4 py-2 text-white bg-white/20 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
                >
                  简体中文
                </button>
                <a
                  href="/en/news"
                  className="px-4 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
                >
                  English
                </a>
              </div>
            </div>

            <a href="/flanexus" className="px-4 py-2 text-white hover:text-blue-200 transition-colors whitespace-nowrap">
              炎禾智联
            </a>

            <a href="/zh/news" className="px-4 py-2 text-white hover:text-blue-200 transition-colors">
              资讯
            </a>

            <a
              href="/zh/contact-us"
              className="px-4 py-2 border border-white rounded-full hover:bg-white hover:text-[#071C74] transition-colors text-white"
            >
              联系我们
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative h-[400px] bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            "url('https://public.readdy.ai/ai/img_res/bc0c3b4702b2e34c67d226621748246a.jpg')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-blue-600/60"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 h-full flex items-center">
          <div className="text-white">
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">最新资讯</h1>
            <p className="text-xl lg:text-2xl opacity-90 max-w-2xl">
              深度洞察亚太地区数字化转型趋势，为您提供最前沿的行业分析和市场动态
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Sidebar - Filters */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* Industry Filter */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-building-line mr-2 text-purple-600"></i>
                  按行业分类
                </h3>
                <div className="space-y-2">
                  {industryCategories.map((industry) => (
                    <button
                      key={industry}
                      onClick={() => handleIndustryFilter(industry)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 ${
                        selectedIndustry === industry
                          ? 'bg-purple-100 text-purple-800 border-2 border-purple-300 font-medium'
                          : 'bg-gray-50 text-gray-700 hover:bg-purple-50 hover:text-purple-700 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{industry}</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">
                            ({getIndustryCategoryCount(industry)})
                          </span>
                          {selectedIndustry === industry && (
                            <i className="ri-check-line text-purple-600"></i>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Country Filter */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-global-line mr-2 text-orange-600"></i>
                  按国家分类
                </h3>
                <div className="space-y-2">
                  {countryCategories.map((country) => (
                    <button
                      key={country}
                      onClick={() => handleCountryFilter(country)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 ${
                        selectedCountry === country
                          ? 'bg-orange-100 text-orange-800 border-2 border-orange-3 00 font-medium'
                          : 'bg-gray-50 text-gray-700 hover:bg-orange-50 hover:text-orange-700 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{country}</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">
                            ({getCountryCategoryCount(country)})
                          </span>
                          {selectedCountry === country && (
                            <i className="ri-check-line text-orange-600"></i>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* WeChat QR Code */}
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">关注我们</h3>
                <img
                  src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/798e5ca3f4e230ffb5a56d977a1ca61a.jpeg"
                  alt="微信二维码"
                  className="w-32 h-32 mx-auto mb-4 rounded-lg"
                />
                <p className="text-sm text-gray-600 text-center">扫码关注微信公众号</p>
                <p className="text-xs text-gray-500 text-center mt-1">获取最新资讯动态</p>
              </div>
            </div>
          </div>

          {/* Right Content - Articles */}
          <div className="lg:col-span-3">
            {/* Filter Status */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <h2 className="text-2xl font-bold text-gray-900">最新资讯</h2>
                  <span className="text-gray-500">共 {filteredArticles.length} 篇文章</span>
                </div>
                {(selectedIndustry !== '全部' || selectedCountry !== '全部') && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">当前筛选：</span>
                    {selectedIndustry !== '全部' && (
                      <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                        {selectedIndustry}
                      </span>
                    )}
                    {selectedCountry !== '全部' && (
                      <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                        {selectedCountry}
                      </span>
                    )}
                    <button
                      onClick={() => {
                        setSelectedIndustry('全部');
                        setSelectedCountry('全部');
                      }}
                      className="text-sm text-gray-500 hover:text-gray-700 underline"
                    >
                      清除筛选
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Articles List */}
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">加载中...</p>
                </div>
              </div>
            ) : filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-gray-400 mb-4">
                  <i className="ri-file-text-line text-6xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">暂无文章</h3>
                <p className="text-gray-600">
                  {selectedIndustry !== '全部' || selectedCountry !== '全部' 
                    ? '该分类下暂时没有文章' 
                    : '暂时没有已发布的文章'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredArticles.map((article) => (
                  <a
                    key={article.id}
                    href={`/zh/news/detail/${article.id}`}
                    className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
                  >
                    <div className="relative h-48 overflow-hidden bg-gray-100">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28article.category%20%20%20%20%20%20%20article.title%29%7D&width=800&height=400&seq=${article.id}&orientation=landscape`;
                        }}
                      />
                      <div className="absolute top-4 left-4">
                        <span
                          className={`px-3 py-1 text-xs font-medium rounded-full ${
                            article.columnType === 'industry'
                              ? 'bg-purple-600 text-white'
                              : 'bg-orange-600 text-white'
                          }`}
                        >
                          {article.columnType === 'industry' ? '行业洞察' : '国家洞察'}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center space-x-2 mb-3">
                        <span
                          className={`px-2 py-1 text-xs rounded ${
                            article.columnType === 'industry'
                              ? 'bg-blue-100 text-blue-600'
                              : 'bg-green-100 text-green-600'
                          }`}
                        >
                          {article.category}
                        </span>
                        <span className="text-sm text-gray-500">{article.date}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-3">{article.summary}</p>
                      <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
                        阅读更多
                        <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform"></i>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default News;
